import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
	BREAKPOINT,
	Palette,
	ResolvedTheme,
	Shadows,
	THEME_STORAGE_KEY,
	ThemeMode,
	dark,
	fonts,
	light,
	radius,
	shadowDark,
	shadowLight,
} from './tokens'

export interface ThemeContextValue {
	/** 用户的选择（三态） */
	mode: ThemeMode
	/** 实际生效的明暗 */
	resolved: ResolvedTheme
	palette: Palette
	shadows: Shadows
	/**
	 * 字体栈与圆角不随明暗变，挂在这里只为让样式工厂拿到**完整**的设计系统：
	 * `useThemedStyles(t => ({...}))` 里少一个 t.fonts 就得回去 import 常量，
	 * 那正是"配色散落"的老毛病换了个形式。
	 */
	fonts: typeof fonts
	radius: typeof radius
	/** 全站断点（px）。挂在主题上，样式工厂里的 media query 就不必再单独 import 常量 */
	breakpoint: number
	setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue>({
	mode: 'system',
	resolved: 'light',
	palette: light,
	shadows: shadowLight,
	fonts,
	radius,
	breakpoint: BREAKPOINT,
	setMode: () => {},
})

export const useTheme = () => useContext(ThemeContext)

/** localStorage 在隐私模式/被禁用时会抛异常，必须包起来——读不到就当跟随系统 */
export function readStoredMode(): ThemeMode {
	try {
		const v = localStorage.getItem(THEME_STORAGE_KEY)
		if (v === 'light' || v === 'dark' || v === 'system') return v
	} catch {
		/* 忽略 */
	}
	return 'system'
}

export function systemPrefersDark(): boolean {
	return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches
}

export function ThemeProvider(props: { children?: React.ReactNode }) {
	const [mode, setModeState] = useState<ThemeMode>(readStoredMode)
	const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark)

	// 跟随系统时，系统配色变了要跟着变
	useEffect(() => {
		if (typeof matchMedia === 'undefined') return
		const mq = matchMedia('(prefers-color-scheme: dark)')
		const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
		mq.addEventListener('change', onChange)
		return () => mq.removeEventListener('change', onChange)
	}, [])

	const resolved: ResolvedTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode

	// 把选择落到 <html> 上：无属性 = 跟随系统，有属性 = 手动
	// CSS 侧由 GlobalStyle 提供 `:root[data-theme="dark"]` 与 `:root:not([data-theme="light"])` 两块
	useEffect(() => {
		const el = document.documentElement
		if (mode === 'system') el.removeAttribute('data-theme')
		else el.setAttribute('data-theme', mode)
	}, [mode])

	const setMode = useCallback((next: ThemeMode) => {
		setModeState(next)
		try {
			localStorage.setItem(THEME_STORAGE_KEY, next)
		} catch {
			/* 忽略：存不下不影响本次会话生效 */
		}
	}, [])

	const value = useMemo<ThemeContextValue>(
		() => ({
			mode,
			resolved,
			palette: resolved === 'dark' ? dark : light,
			shadows: resolved === 'dark' ? shadowDark : shadowLight,
			fonts,
			radius,
			breakpoint: BREAKPOINT,
			setMode,
		}),
		[mode, resolved, setMode]
	)

	return <ThemeContext.Provider value={value}>{props.children}</ThemeContext.Provider>
}
